import React, { useState } from 'react';
import { toast } from 'sonner';
import { ImageIcon, Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import MediaPickerDialog from '@/client/components/cms/MediaPickerDialog';
import {
  HouseSlide,
  HouseSlideInput,
  useHouseSlidesQuery,
  useRemoveHouseSlideMutation,
  useSaveHouseSlideMutation,
} from '@/client/api/promotion-management/house-slides';

const PAGE_LABELS: Record<string, string> = {
  main: 'Explore (home page)',
  market: 'Market',
  homes: 'Homes',
  places: 'Locals',
  login: 'Sign-in (page and pop-up)',
};

// Pages that show only slides made for them: a slide with no page set does not
// spill onto them (ff-api OPT_IN_PAGE_TYPES). Empty, they show lifestyle photos.
const OPT_IN_PAGES = new Set(['login']);

// The hero requests four slots per page; slides beyond that never show.
const HERO_SLOTS = 4;

type Draft = {
  promid?: number;
  page: string;
  name: string;
  description: string;
  link_ext: string;
  asid: number | null;
  image_url: string | null;
  order_by: string;
  date_start: string;
  date_expired: string;
  active: boolean;
};

// API datetimes are UTC; a bare ISO string with no zone would be read as local.
const parseUtc = (v: string | null) => (v ? new Date(/[zZ]|[+-]\d\d:?\d\d$|GMT$/.test(v) ? v : `${v}Z`) : null);

const toLocalInput = (v: string | null) => {
  const d = parseUtc(v);
  if (!d || Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const toIso = (local: string) => (local ? new Date(local).toISOString() : '');

const statusOf = (s: HouseSlide): { label: string; cls: string } => {
  const now = Date.now();
  const start = parseUtc(s.date_start)?.getTime();
  const end = parseUtc(s.date_expired)?.getTime();
  if (!s.active) return { label: 'Off', cls: 'bg-gray-100 text-gray-600' };
  if (end && end <= now) return { label: 'Ended', cls: 'bg-red-50 text-red-700' };
  if (start && start > now) return { label: 'Scheduled', cls: 'bg-amber-50 text-amber-700' };
  return { label: 'Live', cls: 'bg-emerald-50 text-emerald-700' };
};

const emptyDraft = (page: string): Draft => ({
  page, name: '', description: '', link_ext: '', asid: null, image_url: null,
  order_by: '0', date_start: '', date_expired: '', active: true,
});

const SliderContentPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Promotions', href: '/admin/promotions' },
    { label: 'Slider Content', href: '/admin/promotions/slider-content' },
  ]);

  const { data, isLoading, error } = useHouseSlidesQuery();
  const saveMutation = useSaveHouseSlideMutation();
  const removeMutation = useRemoveHouseSlideMutation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const pages = data?.pages ?? Object.keys(PAGE_LABELS);
  const slides = data?.slides ?? [];
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const edit = (s: HouseSlide) =>
    setDraft({
      promid: s.promid,
      page: s.page ?? 'main',
      name: s.name ?? '',
      description: s.description ?? '',
      link_ext: s.link_ext ?? '',
      asid: s.asid,
      image_url: s.image_url,
      order_by: String(s.order_by ?? 0),
      date_start: toLocalInput(s.date_start),
      date_expired: toLocalInput(s.date_expired),
      active: !!s.active,
    });

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) return void toast.error('Add a title');
    if (!draft.asid) return void toast.error('Choose an image');
    const input: HouseSlideInput = {
      page: draft.page,
      name: draft.name.trim(),
      description: draft.description.trim(),
      link_ext: draft.link_ext.trim(),
      asid: draft.asid,
      order_by: Number(draft.order_by) || 0,
      date_start: toIso(draft.date_start),
      date_expired: toIso(draft.date_expired),
      active: draft.active ? 1 : 0,
    };
    try {
      await saveMutation.mutateAsync({ promid: draft.promid, input });
      toast.success('Slide saved');
      setDraft(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the slide'));
    }
  };

  const remove = async (s: HouseSlide) => {
    if (!window.confirm(`Remove "${s.name}" from the slider? This cannot be undone.`)) return;
    try {
      await removeMutation.mutateAsync(s.promid);
      toast.success('Slide removed');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not remove the slide'));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Slider Content</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          Slides each landing page shows when paid promotions do not fill its hero. Paid slides take
          slots first; these fill the rest, in order, up to {HERO_SLOTS} slots.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {getApiErrorMessage(error, 'Could not load slider content')}
        </div>
      )}

      {isLoading && <Skeleton className="h-40 w-full" />}

      {!isLoading && !error && pages.map((page) => {
        const own = slides.filter((s) => s.page === page);
        const liveOwn = own.filter((s) => statusOf(s).label === 'Live');
        // With no live slide of its own, the site falls back to any live slide targeting this page.
        const shared = liveOwn.length
          ? []
          : slides.filter(
              (s) =>
                s.page !== page &&
                statusOf(s).label === 'Live' &&
                (s.pages.includes(page) || (!s.pages.length && !OPT_IN_PAGES.has(page))),
            );
        return (
          <section key={page} className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <header className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{PAGE_LABELS[page] ?? page}</h2>
                <p className="text-xs text-gray-500">
                  {liveOwn.length} live {liveOwn.length === 1 ? 'slide' : 'slides'}
                  {liveOwn.length > HERO_SLOTS && ` — only the first ${HERO_SLOTS} show`}
                </p>
              </div>
              <Button size="sm" onClick={() => setDraft(emptyDraft(page))}>
                <Plus className="mr-1 h-4 w-4" /> Add slide
              </Button>
            </header>

            {!liveOwn.length && (
              <p className="border-b border-gray-100 bg-amber-50 px-5 py-3 text-xs text-amber-800">
                {shared.length
                  ? `No live slide of its own. Visitors currently see shared slides: ${shared.slice(0, HERO_SLOTS).map((s) => s.name).join(', ')}.`
                  : OPT_IN_PAGES.has(page)
                    ? 'No live slide. Visitors see the built-in lifestyle photos until one is added.'
                    : 'No live slide. Visitors see the built-in default image until one is added.'}
              </p>
            )}

            <ul className="divide-y divide-gray-100">
              {own.map((s) => {
                const status = statusOf(s);
                return (
                  <li key={s.promid} className="flex items-center gap-4 px-5 py-3">
                    <div className="h-14 w-24 shrink-0 overflow-hidden rounded-md bg-gray-100">
                      {s.image_url
                        ? <img src={s.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                        : <div className="flex h-full items-center justify-center"><ImageIcon className="h-5 w-5 text-gray-300" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{s.name}</p>
                      <p className="truncate text-xs text-gray-500">{s.description || 'No subtitle'}</p>
                      <p className="truncate text-xs text-gray-400">{s.link_ext || 'No link'}</p>
                    </div>
                    <span className="text-xs text-gray-400">Order {s.order_by}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.cls}`}>{status.label}</span>
                    <Button variant="ghost" size="sm" aria-label={`Edit ${s.name}`} onClick={() => edit(s)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label={`Remove ${s.name}`} onClick={() => remove(s)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      <Dialog open={!!draft} onOpenChange={(v) => { if (!v) setDraft(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft?.promid ? 'Edit slide' : 'Add slide'}</DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="slide-page">Page</Label>
                <select
                  id="slide-page"
                  value={draft.page}
                  onChange={(e) => set({ page: e.target.value })}
                  className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  {pages.map((p) => <option key={p} value={p}>{PAGE_LABELS[p] ?? p}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="slide-title">Title</Label>
                <Input id="slide-title" className="mt-1" maxLength={255} value={draft.name} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="slide-subtitle">Subtitle</Label>
                <Input id="slide-subtitle" className="mt-1" maxLength={300} value={draft.description} onChange={(e) => set({ description: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="slide-link">Link</Label>
                <Input id="slide-link" className="mt-1" placeholder="/market or https://…" value={draft.link_ext} onChange={(e) => set({ link_ext: e.target.value })} />
              </div>
              <div>
                <Label>Image</Label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="h-16 w-28 overflow-hidden rounded-md bg-gray-100">
                    {draft.image_url && <img src={draft.image_url} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                    {draft.asid ? 'Change image' : 'Choose image'}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="slide-start">Starts (optional)</Label>
                  <Input id="slide-start" type="datetime-local" className="mt-1" value={draft.date_start} onChange={(e) => set({ date_start: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="slide-end">Ends (optional)</Label>
                  <Input id="slide-end" type="datetime-local" className="mt-1" value={draft.date_expired} onChange={(e) => set({ date_expired: e.target.value })} />
                </div>
              </div>
              <div className="flex items-end gap-6">
                <div className="w-28">
                  <Label htmlFor="slide-order">Order</Label>
                  <Input id="slide-order" type="number" className="mt-1" value={draft.order_by} onChange={(e) => set({ order_by: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
                  <input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />
                  Show this slide
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
                <Button onClick={save} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving…' : 'Save slide'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <MediaPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(asset) => {
          set({ asid: Number(asset.asid), image_url: asset.public_url ?? null });
          setPickerOpen(false);
        }}
      />
    </div>
  );
};

export default SliderContentPage;
